import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * The one slot at the foot of the viewport on phones and tablets. Either the period
 * and area selector is docked in it, or the quick links to the four tools are, never
 * both: the selector wins on the pages that have one, because it says what the
 * figures on the page describe.
 *
 * The slot's height is published as --cvdp-dock-h on the body, so the page, the
 * cookie banner and the service notice can keep clear of whatever is in it.
 */
@Injectable({ providedIn: 'root' })
export class MobileDockService implements OnDestroy {

  /** Matches the navbar's collapse point, so the header and the dock change together. */
  private readonly compactQuery: string = '(max-width: 991.98px)';

  public readonly compact$ = new BehaviorSubject<boolean>(false);
  /** True while the selector holds the slot, which the quick links give way to. */
  public readonly selectorDocked$ = new BehaviorSubject<boolean>(false);

  private readonly media: MediaQueryList;
  private readonly onMediaChange = (event: MediaQueryListEvent) => this.compact$.next(event.matches);

  constructor() {
    this.media = window.matchMedia(this.compactQuery);
    this.compact$.next(this.media.matches);
    this.media.addEventListener('change', this.onMediaChange);
  }

  public ngOnDestroy(): void {
    this.media.removeEventListener('change', this.onMediaChange);
  }

  public setSelectorDocked(docked: boolean): void {
    this.selectorDocked$.next(docked);
  }

  /** null empties the slot: nothing is docked, so nothing has to keep clear of it. */
  public setHeight(height: number | null): void {
    if (height === null) {
      document.body.classList.remove('cvdp-dock');
      document.body.style.removeProperty('--cvdp-dock-h');
      return;
    }
    document.body.classList.add('cvdp-dock');
    document.body.style.setProperty('--cvdp-dock-h', `${Math.round(height)}px`);
  }
}
